import { useState } from "react";
import { genreOptions, regionOptions, sortOptions, yearOptions } from "@/mocks/categories";

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: string;
}

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  showSort?: boolean;
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function SelectField({ label, value, options, onChange }: SelectFieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-3 pr-8 text-sm text-foreground-800 outline-none transition-colors hover:border-primary-300 focus:border-primary-400"
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <i
          className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
          aria-hidden="true"
        />
      </div>
    </label>
  );
}

export default function FilterBar({ value, onChange, onReset, showSort = true }: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const update = (patch: Partial<FilterState>) => onChange({ ...value, ...patch });

  const fields = (
    <>
      <SelectField
        label="地区"
        value={value.region}
        options={regionOptions}
        onChange={(region) => update({ region })}
      />
      <SelectField
        label="年份"
        value={value.year}
        options={yearOptions}
        onChange={(year) => update({ year })}
      />
      <SelectField
        label="类型"
        value={value.genre}
        options={genreOptions}
        onChange={(genre) => update({ genre })}
      />
      {showSort && (
        <SelectField
          label="排序"
          value={value.sort}
          options={sortOptions}
          onChange={(sort) => update({ sort })}
        />
      )}
    </>
  );

  return (
    <>
      <div className="hidden items-end gap-3 rounded-lg border border-background-200 bg-background-50 p-4 lg:flex">
        <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">{fields}</div>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
        >
          <i className="ri-refresh-line" aria-hidden="true" />
          重置
        </button>
      </div>

      <div className="lg:hidden">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700"
          >
            <i className="ri-filter-3-line" aria-hidden="true" />
            筛选条件
          </button>
          {showSort && (
            <div className="relative flex-1">
              <select
                value={value.sort}
                onChange={(e) => update({ sort: e.target.value })}
                className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2.5 pl-3 pr-8 text-sm text-foreground-800 outline-none"
                aria-label="排序"
              >
                {sortOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <i
                className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex items-end lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="relative w-full rounded-t-xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                筛选条件
              </h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">{fields}</div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  setDrawerOpen(false);
                }}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 py-2.5 text-sm font-medium text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-bold text-background-50"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}